/**
 * br-video-reviews-grid — horizontal rail controls and viewport playback.
 */
(() => {
  const api = window.brVideoReviewsGrid || {};
  if (api.ready) return;

  const SELECTOR = '[data-br-video-reviews-grid]';
  const VIDEO_SELECTOR = '[data-br-video-reviews-video]';
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const sections = new WeakSet();
  const cards = new WeakMap();
  const allVideos = new Set();

  const loadSources = (video) => {
    if (!(video instanceof HTMLVideoElement)) return false;

    const sources = [...video.querySelectorAll('source[data-src]')];
    if (sources.length === 0) return true;

    sources.forEach((source) => {
      source.src = source.dataset.src;
      source.removeAttribute('data-src');
    });

    video.load();
    return true;
  };

  const setIconVisibility = (button, activeClass, inactiveClass, isActive) => {
    const activeIcon = button?.querySelector(activeClass);
    const inactiveIcon = button?.querySelector(inactiveClass);

    if (activeIcon) {
      activeIcon.hidden = !isActive;
      activeIcon.style.display = isActive ? '' : 'none';
    }

    if (inactiveIcon) {
      inactiveIcon.hidden = isActive;
      inactiveIcon.style.display = isActive ? 'none' : '';
    }
  };

  const requestFullscreen = async (state) => {
    try {
      if (state.video.requestFullscreen) {
        await state.video.requestFullscreen();
        return;
      }
    } catch {}

    try {
      if (state.fullscreenTarget.requestFullscreen) {
        await state.fullscreenTarget.requestFullscreen();
        return;
      }
    } catch {}

    if (state.video.webkitEnterFullscreen) {
      state.video.webkitEnterFullscreen();
    }
  };

  const syncPlayControl = (state) => {
    const isPlaying = !state.video.paused && !state.video.ended;

    state.playButton?.setAttribute('aria-pressed', String(isPlaying));
    state.playButton?.setAttribute(
      'aria-label',
      isPlaying ? state.playButton.dataset.labelPause : state.playButton.dataset.labelPlay
    );

    setIconVisibility(
      state.playButton,
      '.br-video-reviews-grid__icon--pause',
      '.br-video-reviews-grid__icon--play',
      isPlaying
    );
  };

  const syncMuteControl = (state) => {
    const isMuted = state.video.muted;

    state.muteButton?.setAttribute('aria-pressed', String(isMuted));
    state.muteButton?.setAttribute(
      'aria-label',
      isMuted ? state.muteButton.dataset.labelUnmute : state.muteButton.dataset.labelMute
    );

    setIconVisibility(
      state.muteButton,
      '.br-video-reviews-grid__icon--muted',
      '.br-video-reviews-grid__icon--sound',
      isMuted
    );
  };

  const requestPlay = (state) => {
    if (document.hidden) return;

    loadSources(state.video);

    const playPromise = state.video.play();
    if (playPromise && typeof playPromise.catch === 'function') {
      playPromise.then(() => syncPlayControl(state)).catch(() => syncPlayControl(state));
    } else {
      syncPlayControl(state);
    }
  };

  const syncPlayback = (state) => {
    if (!state.isVisible || state.userPaused || reducedMotion.matches || document.hidden) {
      state.video.pause();
      syncPlayControl(state);
      return;
    }

    requestPlay(state);
  };

  const muteOtherVideos = (currentVideo) => {
    allVideos.forEach((video) => {
      if (video === currentVideo) return;

      video.muted = true;
      const state = cards.get(video);
      if (state) syncMuteControl(state);
    });
  };

  const setupVideoCard = (item, observer) => {
    const video = item.querySelector(VIDEO_SELECTOR);
    if (!video || cards.has(video)) return;

    const state = {
      item,
      video,
      isVisible: false,
      userPaused: false,
      playButton: item.querySelector('[data-br-video-reviews-play]'),
      muteButton: item.querySelector('[data-br-video-reviews-mute]'),
      fullscreenButton: item.querySelector('[data-br-video-reviews-fullscreen]'),
      fullscreenTarget: item.querySelector('[data-br-video-reviews-media]') || video,
    };

    cards.set(video, state);
    allVideos.add(video);
    syncPlayControl(state);
    syncMuteControl(state);

    window.brVideoDefer?.attach(video, {
      onAttached: () => syncPlayback(state),
    });

    state.playButton?.addEventListener('click', () => {
      if (video.paused || video.ended) {
        state.userPaused = false;
        requestPlay(state);
      } else {
        state.userPaused = true;
        video.pause();
        syncPlayControl(state);
      }
    });

    state.muteButton?.addEventListener('click', () => {
      if (video.muted) {
        muteOtherVideos(video);
        video.muted = false;
      } else {
        video.muted = true;
      }

      syncMuteControl(state);
    });

    state.fullscreenButton?.addEventListener('click', () => requestFullscreen(state));

    video.addEventListener('play', () => syncPlayControl(state));
    video.addEventListener('pause', () => syncPlayControl(state));
    video.addEventListener('volumechange', () => syncMuteControl(state));

    observer?.observe(item);
  };

  const getScrollStep = (track) => {
    const firstCard = track?.querySelector('.br-video-reviews-grid__item');
    if (!firstCard) return 0;

    const styles = window.getComputedStyle(track);
    const gap = Number.parseFloat(styles.columnGap || styles.gap || '0') || 0;
    return firstCard.getBoundingClientRect().width + gap;
  };

  const setupRail = (section) => {
    const viewport = section.querySelector('[data-br-video-reviews-viewport]');
    const track = section.querySelector('[data-br-video-reviews-track]');
    if (!viewport || !track) return;

    const scrollByCard = (direction) => {
      const step = getScrollStep(track);
      if (!step) return;

      viewport.scrollBy({
        left: step * direction,
        behavior: reducedMotion.matches ? 'auto' : 'smooth',
      });
    };

    section
      .querySelector('[data-br-video-reviews-prev]')
      ?.addEventListener('click', () => scrollByCard(-1));
    section
      .querySelector('[data-br-video-reviews-next]')
      ?.addEventListener('click', () => scrollByCard(1));
  };

  const setup = (section) => {
    if (!section || sections.has(section)) return;
    sections.add(section);

    setupRail(section);

    const observer =
      'IntersectionObserver' in window
        ? new IntersectionObserver(
            (entries) => {
              entries.forEach((entry) => {
                const video = entry.target.querySelector(VIDEO_SELECTOR);
                const state = video ? cards.get(video) : null;
                if (!state) return;

                state.isVisible = entry.isIntersecting && entry.intersectionRatio >= 0.5;
                syncPlayback(state);
              });
            },
            { threshold: [0, 0.5, 1] }
          )
        : null;

    section
      .querySelectorAll('.br-video-reviews-grid__item')
      .forEach((item) => setupVideoCard(item, observer));
  };

  const syncAllPlayback = () => {
    allVideos.forEach((video) => {
      const state = cards.get(video);
      if (state) syncPlayback(state);
    });
  };

  const init = (root = document) => {
    root.querySelectorAll?.(SELECTOR).forEach(setup);
  };

  document.addEventListener('visibilitychange', syncAllPlayback);
  reducedMotion.addEventListener('change', syncAllPlayback);
  document.addEventListener('shopify:section:load', (event) => {
    if (event.target?.matches?.(SELECTOR)) setup(event.target);
    init(event.target);
  });

  init();

  window.brVideoReviewsGrid = {
    ready: true,
    init,
    loadSources,
  };
})();
